import React, { useState } from 'react';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { useChargeByIdRelayQuery } from '../../../hooks/transaction/query/V2/useChargesRelay';
import { useWalletTransactionByIdQuery } from '../../../hooks/transaction/query/V2/useWalletTransactionRelay';
import RefundModal from './refundModal';
import {
  convertCentsToMoney,
  getLocaleDateTimeString,
  getPricingString,
  history,
  timeForHumans,
  toTitleCase,
} from '../../../utils';

import { ITransactionInformationProps } from '../../../ts/interface/pages/rentals.interface';
import PendingChargesModal from '../../billing/pendingChargesModal';
import { CardHeader } from '@mui/material';
import { PropertyList } from '../../../components/Redesing/property-list';
import { PropertyListItem } from '../../../components/Redesing/property-list-item';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CardView from '../../../components/Redesing/card-view';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';

const TransactionInformation = ({
  walletTransactionId,
  holdTransactionId,
  transactionId,
  rental,
  admins,
  movaticAccess,
  refundAdminPermission,
}: ITransactionInformationProps) => {
  const [showRefund, setShowRefund] = useState(false);
  const [showChargePendingModal, setShowChargePendingModal] = useState(false);

  // All transaction details now come from GraphQL (same hooks the transaction
  // details page uses). The Stripe charge / hold ids and the wallet transaction
  // id are provided by the rental detail query.
  const { data: cardCharge, isLoading: isLoadingCard } = useChargeByIdRelayQuery(
    transactionId,
    { enabled: Boolean(transactionId) }
  );
  const { data: holdCharge, isLoading: isLoadingHold } = useChargeByIdRelayQuery(
    holdTransactionId,
    { enabled: Boolean(holdTransactionId) }
  );
  const { data: walletTransaction, isLoading: isLoadingWallet } =
    useWalletTransactionByIdQuery(walletTransactionId, {
      enabled: Boolean(walletTransactionId),
    });

  const isLoading =
    (isLoadingCard && Boolean(transactionId)) ||
    (isLoadingHold && Boolean(holdTransactionId)) ||
    (isLoadingWallet && Boolean(walletTransactionId));

  const issueRefund = () => {
    // A charge that is partially refunded keeps `refunded === false` and a
    // `succeeded` status, so it is still refundable for the remaining amount.
    const cardRefundable =
      cardCharge &&
      !cardCharge.refunded &&
      (cardCharge.amount ?? 0) > 0 &&
      cardCharge.status !== 'failed';
    const holdRefundable =
      holdCharge &&
      !holdCharge.refunded &&
      Number(holdCharge.chargeTotal || 0) > 0 &&
      holdCharge.status !== 'failed' &&
      holdCharge.status !== 'requires_capture';
    const walletRefundable =
      walletTransaction &&
      !walletTransaction.refunded &&
      (walletTransaction.amount ?? 0) > 0;

    if (cardRefundable || holdRefundable || walletRefundable) {
      return {
        label: 'Issue Refund',
        onClick: () => setShowRefund(!showRefund),
        disabled: !refundAdminPermission && !movaticAccess,
        startIcon: <KeyboardReturnIcon />,
      };
    }
    return null;
  };

  const TransactionInformationButtonGroup = () => {
    const buttons = [];
    const isChargePending = rental.billing?.pending_charge?.pending;
    if (isChargePending) {
      buttons.push({
        label: 'Pending Charge',
        onClick: () => setShowChargePendingModal(!showChargePendingModal),
        startIcon: <PendingActionsIcon />,
      });
    }
    const refundButton = issueRefund();
    if (refundButton) {
      buttons.push(refundButton);
    }
    return <IconButtonMenu buttons={buttons} />;
  };

  const transactionClick = (event: { item: string }) => {
    history.push(`/billing/transactions/${event.item}`);
  };

  const transactionContent = () => {
    // have both wallet transaction and charge
    return renderWalletAndCCTransaction();
  };

  const renderChargeRefunds = (charge: NonNullable<typeof cardCharge>, title: string) => {
    if (!charge.refunds?.length) {
      return null;
    }
    return (
      <>
        {charge.refunds.map((refund) => (
          <div key={refund.id}>
            <Divider />
            <CardHeader
              sx={{ pl: 2, pt: 3 }}
              title={
                <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                  {`${title} Refund`}
                </div>
              }
            />
            <PropertyList>
              <PropertyListItem
                align="horizontal"
                divider
                label={'Refund ID'}
                value={refund.id}
              />
              <PropertyListItem
                align="horizontal"
                divider
                label={'Refunded On'}
                value={getLocaleDateTimeString(refund.createdOn)}
              />
              <PropertyListItem
                align="horizontal"
                divider
                label={'Refund Status'}
                value={toTitleCase(refund.status)}
              />
              <PropertyListItem
                align="horizontal"
                divider
                label={'Amount'}
                value={convertCentsToMoney(refund.amount)}
              />
              <PropertyListItem
                align="horizontal"
                divider
                label={'Description'}
                value={refund.description || ''}
              />
            </PropertyList>
          </div>
        ))}
      </>
    );
  };

  const renderTransactionsSummary = () => {
    const holdStatus =
      holdCharge &&
      (holdCharge.status === 'requires_capture'
        ? 'Ready for capture'
        : toTitleCase(holdCharge.status));

    return (
      <div>
        {/* wallet transaction info */}
        {!!walletTransaction?.id && (
          <div>
            <CardHeader
              sx={{ pl: 2, pt: 3 }}
              title={
                <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                  Wallet
                </div>
              }
            />
            <PropertyList>
              <PropertyListItem
                align="horizontal"
                divider
                label={'Amount'}
                value={convertCentsToMoney(getWalletTotal(walletTransaction))}
              />
              <PropertyListItem align="horizontal" divider label={'Charge Id'}>
                <Link onClick={() => transactionClick({ item: walletTransaction.id })}>
                  <Typography color="text.secondary" variant="subtitle1">
                    {walletTransaction.id}
                    <i id="table_item_carot" className="fa fa-caret-right" />
                  </Typography>
                </Link>
              </PropertyListItem>
              <PropertyListItem
                divider
                align="horizontal"
                label={'Description'}
                value={walletTransaction.description || ''}
              />
            </PropertyList>
            {(walletTransaction.refunded || !!walletTransaction.amountRefunded) && (
              <>
                <CardHeader
                  sx={{ pl: 2, pt: 3 }}
                  title={
                    <div
                      style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}
                    >
                      Wallet Refund
                    </div>
                  }
                />
                <PropertyList>
                  <PropertyListItem
                    divider
                    align="horizontal"
                    label={'Amount'}
                    value={convertCentsToMoney(walletTransaction.amountRefunded)}
                  />
                  <PropertyListItem
                    divider
                    align="horizontal"
                    label={'Description'}
                    value={walletTransaction.refundDescription || ''}
                  />
                </PropertyList>
              </>
            )}
          </div>
        )}
        {/* stripe charge info */}
        {cardCharge?.id && (
          <div>
            <Divider />
            <CardHeader
              sx={{ pl: 2, pt: 3 }}
              title={
                <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                  Credit Card
                </div>
              }
            />
            <PropertyList>
              <PropertyListItem align="horizontal" divider label={'Charge Id'}>
                <Link onClick={() => transactionClick({ item: cardCharge.id })}>
                  <Typography color="text.secondary" variant="subtitle1">
                    {cardCharge.id}
                    <i id="table_item_carot" className="fa fa-caret-right" />
                  </Typography>
                </Link>
              </PropertyListItem>
              <PropertyListItem
                align="horizontal"
                divider
                label={'Amount'}
                value={convertCentsToMoney(cardCharge.amount)}
              />
              <PropertyListItem
                align="horizontal"
                divider
                label={'Description'}
                value={cardCharge.description || ''}
              />
              <PropertyListItem
                align="horizontal"
                divider
                label={'Status'}
                value={toTitleCase(cardCharge.refundStatus || cardCharge.status)}
              />
            </PropertyList>
            {renderChargeRefunds(cardCharge, 'Credit Card')}
          </div>
        )}
        {/* stripe credit card hold info */}
        {holdCharge?.id && (
          <div>
            <CardHeader
              sx={{ pl: 2, pt: 3 }}
              title={
                <div style={{ minHeight: '36px', display: 'flex', alignItems: 'center' }}>
                  Credit Card Hold
                </div>
              }
            />
            <PropertyList>
              <PropertyListItem align="horizontal" divider label={'Charge Id'}>
                <Link onClick={() => transactionClick({ item: holdCharge.id })}>
                  <Typography color="text.secondary" variant="subtitle1">
                    {holdCharge.id}
                    <i id="table_item_carot" className="fa fa-caret-right" />
                  </Typography>
                </Link>
              </PropertyListItem>
              <PropertyListItem
                align="horizontal"
                divider
                label={'Hold Amount'}
                value={convertCentsToMoney(holdCharge.holdAmount ?? holdCharge.amount)}
              />
              <PropertyListItem
                align="horizontal"
                divider
                label={'Captured Amount'}
                value={convertCentsToMoney(Number(holdCharge.chargeTotal || 0))}
              />
              <PropertyListItem
                align="horizontal"
                divider
                label={'Status'}
                value={holdStatus}
              />
              <PropertyListItem
                align="horizontal"
                label={'Description'}
                value={holdCharge.description || ''}
              />
            </PropertyList>
            {holdCharge.status !== 'canceled' &&
              renderChargeRefunds(holdCharge, 'Credit Card Hold')}
          </div>
        )}
      </div>
    );
  };

  const getWalletTotal = (wallet: NonNullable<typeof walletTransaction>) => {
    return wallet.chargeTotal || wallet.amount || 0;
  };

  const renderWalletAndCCTransaction = () => {
    const pendingChargeDeclined = rental.billing?.pending_charge?.status === false;
    const totalAmount = pendingChargeDeclined ? 0 : rental.billing?.total;
    const rate = rental.billing?.rate;
    const perk = rental.billing?.perk;
    const fees = rental.billing?.fees || [];

    const [preTaxFeeItems, postTaxFeeItems] = fees.reduce(
      (feesArray, customFee) => {
        feesArray[customFee.applied_post_tax ? 1 : 0].push(
          //@ts-ignore
          <PropertyListItem
            align="horizontal"
            key={customFee.id}
            value={convertCentsToMoney(customFee.amount)}
            label={customFee.name}
          />
        );
        return feesArray;
      },
      [[], []]
    );

    const formatCoupon = (value: number, type: string) => {
      switch (type?.toLowerCase()) {
        case 'money':
          return `${convertCentsToMoney(value)} off`;
        case 'percent':
          return `${value}% off`;
        case 'time':
          return `${timeForHumans(value)} free`;
        default:
          return 'N/A';
      }
    };
    const formattedCoupon = formatCoupon(
      rental?.billing?.coupon?.discount as number,
      rental?.billing?.coupon?.type as string
    );

    const getDuration = () => {
      if (rental?.duration === null) {
        return 'Rental is ongoing';
      }
      const duration = timeForHumans(rental.duration);
      return duration === 0 ? '0 Seconds' : duration;
    };

    const UsageDetails = () => {
      return (
        <>
          <PropertyListItem
            align="horizontal"
            data-id={'rental-duration'}
            value={getDuration()}
            label="Rental Duration"
          />
          {/* Rate info */}
          {rate?.id ? (
            <div>
              <Divider />
              <PropertyListItem align="horizontal" label={'Rate'}>
                <Link onClick={() => history.push(`/billing/rates/${rate.old_id}`)}>
                  <Typography color="text.secondary" variant="subtitle1">
                    {rate.name}
                    <i id="table_item_carot" className="fa fa-caret-right" />
                  </Typography>
                </Link>
                <br />
                <Typography color="text.secondary" variant="subtitle1">
                  {getPricingString(rate)}
                </Typography>
              </PropertyListItem>
            </div>
          ) : null}
          {/* perk info */}
          {perk?.id ? (
            <div>
              <Divider />
              <PropertyListItem align="horizontal" label={'Membership'}>
                <Link
                  onClick={() =>
                    history.push(`/memberships/${perk.membership_id}/general`)
                  }
                >
                  <Typography color="text.secondary" variant="subtitle1">
                    {perk.membership_name}
                    <i id="table_item_carot" className="fa fa-caret-right" />
                  </Typography>
                </Link>
                {perk.free_time ? (
                  <>
                    <br />

                    <Typography color="text.secondary" variant="subtitle1">
                      {`${timeForHumans(perk.free_time)} free`}
                    </Typography>
                  </>
                ) : null}
                {perk.money_off ? (
                  <>
                    <br />

                    <Typography color="text.secondary" variant="subtitle1">
                      {`$${(perk.money_off / 100).toFixed(2)} Off`}
                    </Typography>
                  </>
                ) : null}
                {perk.discount ? (
                  <>
                    <br />

                    <Typography color="text.secondary" variant="subtitle1">
                      {`${perk.discount}% Off`}
                    </Typography>
                  </>
                ) : null}
              </PropertyListItem>
            </div>
          ) : null}
          {/* coupon info */}
          {rental.billing?.coupon?.id ? (
            <div>
              <Divider />
              <PropertyListItem align="horizontal" label={'Coupon'}>
                <Link
                  onClick={() =>
                    history.push(`/billing/coupon/${rental.billing.coupon.old_id}`)
                  }
                >
                  <Typography color="text.secondary" variant="subtitle1">
                    {rental.billing.coupon.name}
                    <i id="table_item_carot" className="fa fa-caret-right" />
                  </Typography>
                </Link>
                <br />

                <Typography color="text.secondary" variant="subtitle1">
                  {formattedCoupon}
                </Typography>
              </PropertyListItem>
            </div>
          ) : null}
        </>
      );
    };

    if (rental.ended_within_grace_period) {
      return (
        <>
          <UsageDetails />
          <Divider />
          <PropertyListItem align="horizontal" data-id={'rental-duration'} label="">
            <SeverityPill color={'info'}>Ended within the grace period.</SeverityPill>
          </PropertyListItem>
        </>
      );
    }

    if (rental.billing?.void_charge) {
      const voidedByAdminName = rental?.billing?.void_by?.full_name || 'Admin';
      const voidedByAdminEmail = rental?.billing?.void_by?.email;
      const voidedOn = rental?.billing?.voided_on;
      const voidedReason = rental?.billing?.void_reason;
      const voidByAdminIsOnSystem = !!admins.find(
        (admin: { email: string }) => admin.email === voidedByAdminEmail
      );

      return (
        <>
          <UsageDetails />
          <Divider />
          {voidedOn ? (
            <PropertyListItem
              align="horizontal"
              label="Voided on"
              value={getLocaleDateTimeString(voidedOn)}
            />
          ) : null}
          <PropertyListItem align="horizontal" divider label={'Voided by'}>
            <Link
              onClick={() =>
                voidByAdminIsOnSystem
                  ? () => history.push(`/settings/admin/${voidedByAdminEmail}`)
                  : null
              }
            >
              <Typography color="text.secondary" variant="subtitle1">
                {voidedByAdminName +
                  (voidByAdminIsOnSystem ? '' : ` (${voidedByAdminEmail})`)}
                <i id="table_item_carot" className="fa fa-caret-right" />
              </Typography>
            </Link>
            <br />

            <Typography color="text.secondary" variant="subtitle1">
              {formattedCoupon}
            </Typography>
          </PropertyListItem>
          {voidedReason && (
            <PropertyListItem
              align="horizontal"
              label="Voided reason"
              value={voidedReason}
            />
          )}
        </>
      );
    }

    return (
      <div>
        <UsageDetails />
        {/* General info */}
        {!!rental.end?.time && (
          <>
            <Divider />
            <PropertyListItem
              align="horizontal"
              label="Usage Subtotal"
              value={convertCentsToMoney(rental?.billing?.usage_sub_total)}
            />
            {!!rental.billing?.parking_credit && (
              <PropertyListItem
                align="horizontal"
                label={
                  rental.billing.parking_credit < 0 ? 'Parking Credit' : 'Parking Fee'
                }
                value={convertCentsToMoney(rental.billing.parking_credit)}
              />
            )}
            <>{preTaxFeeItems}</>
            {!!rental.billing?.admin_modification_reason && (
              <PropertyListItem align="horizontal" label="Admin Modification">
                <Typography color="text.secondary" variant="subtitle1">
                  {convertCentsToMoney(rental.billing.admin_modification)}
                </Typography>
                {!!rental.billing?.admin_modification_reason && (
                  <>
                    <br />
                    <Typography color="text.secondary" variant="subtitle1">
                      {rental.billing.admin_modification_reason}
                    </Typography>
                  </>
                )}
              </PropertyListItem>
            )}
            <Divider />
            <PropertyListItem align="horizontal" label="Subtotal">
              <Typography color="text.secondary" variant="subtitle1">
                {convertCentsToMoney(rental.billing.sub_total)}
              </Typography>
              {rental.billing.pending_charge?.pending ? (
                <>
                  <br />

                  <Typography color="text.secondary" variant="subtitle1">
                    Pending admin approval
                  </Typography>
                </>
              ) : null}
              {rental.billing.pending_charge?.decision_date ? (
                <>
                  <br />

                  <Typography color="text.secondary" variant="subtitle1">
                    {`${
                      rental.billing.pending_charge.status ? 'Accepted' : 'Declined'
                    } by admin on ${getLocaleDateTimeString(
                      rental.billing.pending_charge.decision_date
                    )}`}
                  </Typography>
                  <br />

                  <Typography color="text.secondary" variant="subtitle1">
                    {rental.billing.pending_charge.note}
                  </Typography>
                </>
              ) : null}
            </PropertyListItem>
            {typeof rental.billing.tax_rate === 'number' && (
              <>
                <PropertyListItem
                  align="horizontal"
                  value={`${convertCentsToMoney(rental.billing.tax_amount)} (${
                    rental.billing.tax_rate.toFixed(2) // TODO: Remove this rounding once this ticket gets resolved: https://movatic.atlassian.net/browse/BUGS-150
                  }%)`}
                  label="Tax"
                />
              </>
            )}
            <>{postTaxFeeItems}</>
            <Divider />
            <PropertyListItem
              align="horizontal"
              value={convertCentsToMoney(totalAmount)}
              label={'Total'}
            />
          </>
        )}
        {(walletTransaction?.id || cardCharge?.id || holdCharge?.id) && (
          <div>{renderTransactionsSummary()}</div>
        )}
        <PendingChargesModal
          handleModal={(value: boolean) => setShowChargePendingModal(value)}
          rentalId={rental.old_id}
          rentalAmount={rental.billing?.sub_total}
          show={showChargePendingModal}
        />
      </div>
    );
  };

  return (
    <div>
      <CardView
        headerActions={TransactionInformationButtonGroup()}
        isLoading={isLoading}
        content={
          <>
            {transactionContent()}
            {showRefund && (
              <RefundModal
                show={showRefund}
                userName={rental.user?.full_name}
                close={(redirect?: boolean) => {
                  setShowRefund(false);
                  if (redirect) {
                    history.push(`/rentals`);
                  }
                }}
                rentalId={rental.id}
                chargeId={cardCharge?.id || holdCharge?.id}
                walletTransactionId={walletTransaction?.oldId || walletTransaction?.id}
                refundType={'rental'}
                amount={rental.billing?.total}
                cents={cardCharge?.amount || Number(holdCharge?.chargeTotal || 0) || 0}
              />
            )}
          </>
        }
        title={'Transaction Details'}
      />
    </div>
  );
};

export default TransactionInformation;
